import React, { ComponentPropsWithoutRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyCard from './CompanyCard';
import AutoComplete from 'react-google-autocomplete';
import LocationIcon from '../../assets/icons/LocationIcon';
import useTheme from '../../hooks/ui/useTheme';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import { GooglePlaceResource } from '../../services/resources/GooglePlaceResource';
import { CompanyDetailsInterface, Franchise } from '../../services/exports/Interfaces';
import collect from 'collect.js';
import { useLocationData } from './provider/LocationDataProvider';
import DeliveryAddressForm from './form/DeliveryAddressForm';
import { ORDER_METHODS } from '../../services/exports/Constants';
import useDeliveryCompatibleStuff from './useDeliveryCompatibleStuff';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import LoadingIndicator from '../../components-library/loading/LoadingIndicator';
import { useFormContext } from 'react-hook-form';
import { DeliveryAddressSchema } from '../../modules/mobile/modals/delivery-address/form/schema';
import InfoIcon from '../../assets/logo/InfoIcon';
import classnames from 'classnames';

export interface CompanyDistanceInterface {
  company: CompanyDetailsInterface;
  distance?: number;
}

interface Props extends ComponentPropsWithoutRef<'section'> {
  data: Franchise;
}

export default function CompaniesList(props: Props) {
  const { data, className, ...rest } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:CompaniesList' });
  const { t: tErrors } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const deliveryAddressForm = useFormContext<DeliveryAddressSchema>();
  const { orderMethod, validationError, setCompany, company } = useLocationData();
  const { isSubmitSuccessful, isSubmitting } = deliveryAddressForm.formState;
  const theme = useTheme();
  const [addressSearch, setAddressSearch] = useState('');
  const [place, setPlace] = useState<GooglePlaceResource>(null);
  const { queryState, isLoading, handle } = useDeliveryCompatibleStuff();
  const hasDeliveryAddressForm =
    orderMethod === ORDER_METHODS.DELIVERY && place && !isSubmitSuccessful && !isSubmitting;
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const deliveryCompatibleCompanies = queryState.data;

  const companies = useMemo<CompanyDistanceInterface[]>(
    () =>
      (orderMethod !== ORDER_METHODS.DELIVERY || place) &&
      collect(data?.companies ?? [])
        .when(orderMethod === ORDER_METHODS.DELIVERY, (companies) =>
          companies.whereIn('id', deliveryCompatibleCompanies ?? []),
        )
        .when(orderMethod === ORDER_METHODS.ROOM_SERVICE, (companies) =>
          companies.filter((company) => company.has_room_service),
        )
        .map((item) => {
          const distance = place?.calculateDistance({
            lat: item.lat,
            lng: item.lon,
          });

          return {
            company: item,
            distance,
          };
        })
        .sortBy('distance')
        .toArray(),
    [data?.companies, place, deliveryCompatibleCompanies, orderMethod],
  );

  const onPlaceSelected = async (placeResult: google.maps.places.PlaceResult): Promise<void> => {
    const selectedPlaceResource = new GooglePlaceResource(placeResult);

    deliveryAddressForm.setValue('delivery_street_name', selectedPlaceResource.getStreetName());
    deliveryAddressForm.setValue('delivery_street_number', selectedPlaceResource.getStreetNumber());
    deliveryAddressForm.setValue('delivery_city', selectedPlaceResource.getCity());
    deliveryAddressForm.setValue('delivery_zip_code', selectedPlaceResource.getPostalCode());
    deliveryAddressForm.setValue('delivery_lat', selectedPlaceResource.getLat());
    deliveryAddressForm.setValue('delivery_lng', selectedPlaceResource.getLng());

    setAddressSearch(selectedPlaceResource.formatAddress());
    setPlace(selectedPlaceResource);

    await handle(selectedPlaceResource);
  };
  const clearAddress = () => {
    deliveryAddressForm.reset({
      delivery_lng: undefined,
      delivery_street_number: '',
      delivery_zip_code: '',
      delivery_lat: undefined,
      delivery_city: '',
      delivery_street_name: '',
    });
    setAddressSearch('');
    setPlace(null);
    setCompany(null);
  };

  useEffect(() => {
    if (!place) {
      return;
    }

    handle(place);
  }, [place, orderMethod]);

  useEffect(() => {
    async function setInitialPlace(): Promise<void> {
      if (
        !isOrderMethodInfoComplete ||
        !deliveryAddressForm?.getValues('delivery_lat') ||
        !deliveryAddressForm?.getValues('delivery_lng')
      ) {
        return;
      }

      const { success, location } = await GooglePlaceResource.getPlaceFromCoordinates({
        lat: deliveryAddressForm?.getValues('delivery_lat'),
        lng: deliveryAddressForm?.getValues('delivery_lng'),
      });

      if (!success) {
        return;
      }

      const locationResource = new GooglePlaceResource(location);

      setPlace(locationResource);
      setAddressSearch(locationResource.formatAddress());
    }

    setInitialPlace();
  }, []);

  useEffect(() => {
    if (orderMethod !== ORDER_METHODS.DELIVERY) {
      return;
    }

    if (!place || isSubmitSuccessful) {
      return;
    }

    handle(place);
  }, [orderMethod]);

  const scrollToCompany = (id: number): void => {
    const element = document.getElementById(`company-${id}`);
    if (!element) return;
    element.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  useLayoutEffect(() => {
    if (!company?.id || companies?.length === 0 || isLoading) return;
    scrollToCompany(company.id);
  }, [companies, isLoading]);

  const searchPlaceholder = t(
    orderMethod === ORDER_METHODS.DELIVERY ? 'form.address.delivery.placeholder' : 'form.address.placeholder',
  );
  const hasNoDeliveryCompaniesLabel =
    orderMethod === ORDER_METHODS.DELIVERY &&
    !queryState.isUninitialized &&
    !queryState.isLoading &&
    !queryState?.data?.length;

  const SearchBar = (): JSX.Element | null => {
    if (orderMethod === ORDER_METHODS.PICKUP && !data?.has_multiple_companies) {
      return null;
    }

    return (
      <div className="flex items-center w-full px-md py-sm bordered space-x-md">
        {isLoading ? (
          <LoadingIndicator className="size-2" size={16} />
        ) : (
          <LocationIcon className="size-4" color={theme.color.gray['700']} />
        )}
        <AutoComplete
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          onPlaceSelected={onPlaceSelected}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressSearch(e.target.value)}
          value={addressSearch}
          placeholder={searchPlaceholder}
          className="w-full text-gray-700 text-sm overflow-ellipsis placeholder:text-[#777777]"
          options={{
            types: ['address'],
            componentRestrictions: {
              country: data?.country,
            },
          }}
        />
        {!!addressSearch?.length && (
          <div
            className="bg-gray-700 rounded-full flex flex-none justify-center items-center size-4 cursor-pointer"
            onClick={clearAddress}
          >
            <CloseIcon className="size-2" color={theme.color.gray['500']} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <section className={classnames('space-y-md flex flex-col', className)} {...rest}>
        {SearchBar()}
        {hasNoDeliveryCompaniesLabel && (
          <p className="bg-[#BC4C6B26]/15 px-sm py-[10px] rounded-lg text-[#BC4C6B] font-semibold text-sm flex items-center gap-x-sm">
            <InfoIcon color="#BC4C6B" />
            {t('errors.no_delivery_companies')}
          </p>
        )}
        {hasDeliveryAddressForm && <DeliveryAddressForm />}
        {validationError && <p className="text-danger text-sm">{tErrors(`error_messages.${validationError}`)}</p>}
        {!isLoading &&
          companies?.map((item, index) => (
            <div key={`${item.company.id}${orderMethod}`} id={`company-${item.company.id}`}>
              <CompanyCard data={item} className={classnames('pt-md', !!index && 'border-t')} />
            </div>
          ))}
      </section>
    </>
  );
}
